import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { graphql, useRefetchableFragment, useLazyLoadQuery } from "react-relay";
import type { AuthContext_user$key } from "./__generated__/AuthContext_user.graphql";

export const useAuthenticated = (queryRef: AuthContext_user$key): {
  user: { id: string; firstName: string | null | undefined; lastName: string | null | undefined; email: string | null | undefined } | null;
  isAuthenticated: boolean;
  refetch: (variables?: Record<string, unknown>) => void;
} => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasBeenAuthenticatedRef = useRef(false);

  const [data, refetch] = useRefetchableFragment(graphql`
    fragment AuthContext_user on Query
    @refetchable(queryName: "AuthContextRefetchQuery") {
      me {
        id
        firstName
        lastName
        email
      }
    }
  `, queryRef);

  const user = data?.me ?? null;

  const hasUser = !!user?.id;

  if (hasUser) {
    hasBeenAuthenticatedRef.current = true;
  }

  const redirectIfNotAuthenticated = (): void => {
    const isOnLoginRoute =
      location.pathname.startsWith("/login") || location.pathname === "/";
    const isRegisterRoute = location.pathname.startsWith("/register");
    const isDashboardRoute = location.pathname.startsWith("/dashboard");

    if (hasUser && (isOnLoginRoute || isRegisterRoute)) {
      navigate("/dashboard/home", { replace: true });
      return;
    }

    if (!hasUser) {
      if (isDashboardRoute && hasBeenAuthenticatedRef.current) {
        return;
      }
      if (!isOnLoginRoute && !isRegisterRoute) {
        navigate("/login", { replace: false });
      }
    }
  };

  useEffect(() => {
    redirectIfNotAuthenticated();
  }, [hasUser, location.pathname]);

  return {
    user,
    isAuthenticated: hasUser,
    refetch: () => refetch({}),
  };
};

export const useAuthState = (): {
  user: { id: string; firstName: string | null | undefined; lastName: string | null | undefined; email: string | null | undefined } | null;
  isAuthenticated: boolean;
  refetch: () => void;
  logout: () => void;
} => {
  const queryData = useLazyLoadQuery(
    graphql`
      query AuthContextMeQuery {
        ...AuthContext_user
      }
    `,
    {},
    { fetchPolicy: "store-or-network" }
  );

  const authResult = useAuthenticated(queryData as AuthContext_user$key);

  const logout = () => {
    localStorage.removeItem("pix2bit:token");
    window.location.href = "/";
  };

  return {
    ...authResult,
    logout,
  };
};
